<template>
  <v-row justify="center">
    <!-- ============================================================= -->
    <!-- ===================== LIST VIEW ============================== -->
    <!-- ============================================================= -->
    <v-col v-show="!dialog" cols="12" class="pa-5">
      <!-- ===== Premium Header ===== -->
      <div class="d-flex align-center mb-4">
        <v-avatar color="primary lighten-4" size="48" class="mr-3 elevation-1">
          <v-icon color="primary" size="26">mdi-account-group</v-icon>
        </v-avatar>
        <div>
          <h1 class="text-h5 font-weight-black grey--text text--darken-3 mb-0">
            {{ type == 1 ? 'Contactos' : type == 2 ? 'Clientes' : 'Suplidores' }}
          </h1>
          <p class="text-caption grey--text mb-0">
            Crea tus clientes, proveedores y demás contactos para asociarlos en tus documentos.
          </p>
        </div>
      </div>

      <!-- ===== Top bar: total / activos / inactivos ===== -->
      <v-card outlined class="rounded-lg mb-2">
        <div class="d-flex flex-wrap align-center">
          <div class="cs-stat px-4 py-2 d-flex align-center" style="gap: 10px">
            <span class="text-caption grey--text text-uppercase font-weight-medium">Total contactos</span>
            <span class="text-h6 font-weight-bold grey--text text--darken-3">{{ stats.total.toLocaleString() }}</span>
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex align-center px-4 py-2" style="gap: 8px">
            <v-chip color="success lighten-4" text-color="success darken-2" small label>
              <v-icon left x-small>mdi-check-circle</v-icon>
              Activos: {{ stats.active.toLocaleString() }}
            </v-chip>
            <v-chip color="orange lighten-4" text-color="orange darken-2" small label>
              <v-icon left x-small>mdi-minus-circle</v-icon>
              Inactivos: {{ stats.inactive.toLocaleString() }}
            </v-chip>
          </div>
        </div>
      </v-card>

      <!-- ===== Cards por rol: Clientes / Suplidores ===== -->
      <v-row class="mb-4" dense>
        <!-- Clientes -->
        <v-col v-if="type !== 3" cols="12" :md="type === 1 ? 6 : 12">
          <v-card outlined class="rounded-lg fill-height d-flex align-center px-3 py-2">
            <div class="d-flex align-center mr-4">
              <v-avatar color="blue lighten-5" size="36" class="mr-2">
                <v-icon small color="blue darken-1">mdi-account-tie</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption blue--text text--darken-1 text-uppercase font-weight-medium" style="line-height: 1">Clientes</div>
                <div class="text-h5 font-weight-bold blue--text text--darken-1" style="line-height: 1.1">{{ stats.customers.toLocaleString() }}</div>
              </div>
            </div>
            <v-divider vertical class="my-1"></v-divider>
            <v-row no-gutters class="flex-grow-1">
              <v-col v-for="t in customerTypes" :key="'c-' + t.name" cols="3" class="text-center px-1">
                <div class="text-caption grey--text text-truncate" style="line-height: 1">{{ t.name }}</div>
                <div class="text-subtitle-1 font-weight-bold blue--text text--darken-1" style="line-height: 1.2">{{ t.count.toLocaleString() }}</div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <!-- Suplidores -->
        <v-col v-if="type !== 2" cols="12" :md="type === 1 ? 6 : 12">
          <v-card outlined class="rounded-lg fill-height d-flex align-center px-3 py-2">
            <div class="d-flex align-center mr-4">
              <v-avatar color="purple lighten-5" size="36" class="mr-2">
                <v-icon small color="purple darken-1">mdi-truck-delivery</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption purple--text text--darken-1 text-uppercase font-weight-medium" style="line-height: 1">Suplidores</div>
                <div class="text-h5 font-weight-bold purple--text text--darken-1" style="line-height: 1.1">{{ stats.suppliers.toLocaleString() }}</div>
              </div>
            </div>
            <v-divider vertical class="my-1"></v-divider>
            <v-row no-gutters class="flex-grow-1">
              <v-col v-for="t in supplierTypes" :key="'s-' + t.name" cols="3" class="text-center px-1">
                <div class="text-caption grey--text text-truncate" style="line-height: 1">{{ t.name }}</div>
                <div class="text-subtitle-1 font-weight-bold purple--text text--darken-1" style="line-height: 1.2">{{ t.count.toLocaleString() }}</div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="rounded-lg">
        <v-tabs v-model="listTab" color="primary" show-arrows>
          <v-tab>
            <v-icon left small>mdi-account-group</v-icon>
            {{ type == 1 ? 'Contactos' : type == 2 ? 'Clientes' : 'Suplidores' }}
          </v-tab>
          <v-tab v-if="canViewDuplicates">
            <v-icon left small>mdi-content-duplicate</v-icon>
            Posibles duplicados
            <v-chip
              v-if="duplicatesLoaded"
              x-small
              label
              class="ml-2"
              color="orange lighten-4"
              text-color="orange darken-2"
            >
              {{ duplicates.length }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <v-tabs-items v-model="listTab">
          <!-- ===== TAB: Lista de entidades ===== -->
          <v-tab-item>
          <v-data-table
            :headers="headers"
            :items="filteredEntities"
            :loading="loading"
            sort-by="calories"
            class="elevation-0"
          >
            <template v-slot:[`item.name`]="{ item }">
              <div class="d-flex align-center py-1" @click="viewEntity(item.entity_id)" style="cursor:pointer">
                <v-avatar size="32" color="primary lighten-4" class="mr-2">
                  <span class="primary--text font-weight-bold caption">{{ item.name.charAt(0) }}</span>
                </v-avatar>
                <span class="font-weight-medium text-body-2 grey--text text--darken-3 cs-link">{{ item.name }}</span>
              </div>
            </template>
            <template v-slot:[`item.document_id`]="{ item }">
              <v-chip label x-small color="blue-grey lighten-5" class="font-weight-bold">
                <v-icon left x-small color="primary">mdi-card-account-details</v-icon>
                {{ item.document_id }}
              </v-chip>
            </template>
            <template v-slot:[`item.role`]="{ item }">
              <div class="d-flex align-center" style="gap:4px">
                <v-chip v-if="item.is_customer" x-small color="blue lighten-4" text-color="blue darken-2" label class="font-weight-bold">
                  <v-icon left x-small>mdi-account-tie</v-icon>Cliente
                </v-chip>
                <v-chip v-if="item.is_supplier" x-small color="purple lighten-4" text-color="purple darken-2" label class="font-weight-bold">
                  <v-icon left x-small>mdi-truck-delivery</v-icon>Suplidor
                </v-chip>
              </div>
            </template>
            <template v-slot:top>
              <v-toolbar flat class="px-2">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="Búsqueda rápida..."
                  outlined
                  rounded
                  dense
                  hide-details
                  class="mr-4"
                  style="max-width: 380px"
                  :loading="searchWaiting"
                >
                  <template v-slot:progress>
                    <v-progress-linear
                      :value="searchProgress"
                      color="primary"
                      height="3"
                      absolute
                      bottom
                      rounded
                    ></v-progress-linear>
                  </template>
                </v-text-field>
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  label="Estado"
                  outlined
                  rounded
                  dense
                  hide-details
                  class="mr-3"
                  style="max-width: 220px"
                ></v-select>
                <v-select
                  v-model="filters.role"
                  :items="roleOptions"
                  label="Rol"
                  outlined
                  rounded
                  dense
                  hide-details
                  class="mr-3"
                  style="max-width: 220px"
                ></v-select>
                <v-spacer></v-spacer>
                <v-btn
                  @click="newItem"
                  v-if="type == 1 && $store.state.auth.globalFeatures.includes('CNN-013')"
                  color="primary"
                  dark
                  class="mb-2"
                >
                  <v-icon small>add</v-icon>
                  <span v-if="type == 1"> Nuevo Contacto</span>
                  <span v-if="type == 2"> Nuevo Cliente</span>
                  <span v-if="type == 3"> Nuevo Suplidor</span>
                </v-btn>
                <v-dialog v-model="dialogDelete" max-width="500px">
                  <v-card>
                    <v-card-title class="text-h5"
                      >Are you sure you want to delete this item?</v-card-title
                    >
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="blue darken-1" text @click="closeDelete"
                        >Cancel</v-btn
                      >
                      <v-btn color="blue darken-1" text @click="deleteItemConfirm"
                        >OK</v-btn
                      >
                      <v-spacer></v-spacer>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-toolbar>
            </template>
            <template v-slot:[`item.status_id`]="{ item }">
              <v-chip
                x-small
                :color="item.status_id ? 'success lighten-4' : 'red lighten-4'"
                :text-color="item.status_id ? 'success darken-2' : 'red darken-2'"
                label
                class="font-weight-bold"
              >
                <v-icon left x-small>{{ item.status_id ? 'mdi-check-circle' : 'mdi-minus-circle' }}</v-icon>
                {{ item.status_id ? 'Activo' : 'Inactivo' }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-if="$store.state.auth.globalFeatures.includes('CNN-013')"
                    @click="editItem(item)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Editar entidad</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="showDialogStatus(item)">
                    <v-list-item-icon class="mr-3">
                      <v-icon small :color="item.status_id ? 'red' : 'success'">
                        {{ item.status_id ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ item.status_id ? 'Desactivar' : 'Activar' }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-slot:no-data>
              <v-btn color="primary" @click="initialize"> Reset </v-btn>
            </template>
          </v-data-table>
          </v-tab-item>

          <!-- ===== TAB: Posibles duplicados ===== -->
          <v-tab-item v-if="canViewDuplicates">
            <div class="pa-4">
              <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
                <v-icon color="orange darken-2">mdi-content-duplicate</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2">
                  Posibles entidades duplicadas
                </span>
                <v-chip
                  v-if="duplicatesLoaded"
                  small
                  label
                  color="orange lighten-4"
                  text-color="orange darken-2"
                >
                  {{ duplicates.length }} coincidencia(s) ≥ 95%
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn
                  @click="loadDuplicates"
                  :loading="loadingDuplicates"
                  color="orange darken-2"
                  depressed
                >
                  <v-icon small left>{{ duplicatesLoaded ? 'mdi-refresh' : 'mdi-play' }}</v-icon>
                  {{ duplicatesLoaded ? 'Actualizar reporte' : 'Generar reporte' }}
                </v-btn>
              </div>

              <v-alert
                v-if="!duplicatesLoaded && !loadingDuplicates"
                type="info"
                text
                dense
                border="left"
                class="mb-0"
              >
                Compara todas las entidades por <strong>nombre</strong> (≥ 95% de similitud) y por
                <strong>documento</strong> normalizado. La consulta puede tardar cerca de un minuto.
              </v-alert>

              <div v-else-if="loadingDuplicates" class="py-10 text-center">
                <v-progress-circular indeterminate color="orange darken-2" size="48"></v-progress-circular>
                <div class="mt-3 text-body-2 grey--text">
                  Generando reporte... esto puede tardar cerca de un minuto.
                </div>
              </div>

              <v-data-table
                v-else
                :headers="duplicatesHeaders"
                :items="duplicates"
                :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                class="elevation-0"
                no-data-text="No se encontraron duplicados con 95% o más de coincidencia."
              >
                <template v-slot:[`item.entity_a`]="{ item }">
                  <div class="font-weight-medium grey--text text--darken-3">{{ item.name_a }}</div>
                  <div class="text-caption grey--text">#{{ item.id_a }} · {{ item.doc_a }}</div>
                </template>
                <template v-slot:[`item.entity_b`]="{ item }">
                  <div class="font-weight-medium grey--text text--darken-3">{{ item.name_b }}</div>
                  <div class="text-caption grey--text">#{{ item.id_b }} · {{ item.doc_b }}</div>
                </template>
                <template v-slot:[`item.score`]="{ item }">
                  <v-chip
                    small label class="font-weight-bold"
                    :color="item.score >= 98 ? 'red lighten-4' : 'orange lighten-4'"
                    :text-color="item.score >= 98 ? 'red darken-2' : 'orange darken-3'"
                  >
                    {{ item.score }}%
                  </v-chip>
                </template>
                <template v-slot:[`item.match_by`]="{ item }">
                  <v-chip x-small label color="blue-grey lighten-5" text-color="blue-grey darken-2">
                    {{ item.match_by }}
                  </v-chip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn
                    small
                    depressed
                    color="primary"
                    :loading="compareLoadingId === item.id_a + '-' + item.id_b"
                    @click="openCompare(item)"
                  >
                    <v-icon x-small left>mdi-compare-horizontal</v-icon> Comparar
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>

    <!-- ============================================================= -->
    <!-- ============== FORM VIEW (full page inline) ================= -->
    <!-- ============================================================= -->
    <v-col v-if="dialog" cols="12" class="pa-0">
      <v-container fluid class="px-5 pt-3">
      <!-- Sticky action bar -->
      <v-card flat class="cs-form-bar elevation-1 mb-4 rounded-lg">
        <v-toolbar flat color="transparent" class="px-2 rounded-lg">
          <v-btn icon @click="close">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-avatar color="primary lighten-4" size="42" class="mr-3">
            <v-icon color="primary">group</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold grey--text text--darken-3">{{ formTitle }}</div>
            <div class="text-caption grey--text d-none d-sm-block">
              Completa la información para asociarlo en tus documentos.
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn outlined class="mr-3" @click="close">Cancelar</v-btn>
          <v-btn color="primary" depressed @click="save">
            <v-icon left small>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-toolbar>
      </v-card>

        <v-row>
          <!-- ===================== LEFT COLUMN ===================== -->
          <v-col cols="12" md="8">
            <!-- General information -->
            <v-card class="rounded-lg mb-4" outlined>
              <v-card-title class="cs-section-title">
                <v-icon left color="primary">mdi-card-account-details-outline</v-icon>
                Identificación
              </v-card-title>
              <v-divider></v-divider>
              <v-container class="px-5 py-4">
                <v-row>
                  <v-col cols="12" sm="4" md="4">
                    <v-autocomplete
                      dense
                      outlined
                      v-model="selectedDocument"
                      :items="documents"
                      color="cyanText"
                      label="Tipo de identificación"
                      item-text="name"
                      item-value="id"
                      prepend-inner-icon="mdi-card-account-details-outline"
                      return-object
                      :readonly="editedItem.entity_id != null"
                      hide-details="auto"
                      required
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="8" md="5">
                    <v-text-field
                      :error-messages="
                        rncValid == true && selectedDocument && selectedDocument.value == 1
                          ? 'El rnc debe ser valido'
                          : ''
                      "
                      :readonly="editedItem.entity_id != null"
                      v-model="editedItem.document_id"
                      :label="
                        selectedDocument && selectedDocument.name
                          ? selectedDocument.name + ' número'
                          : 'Número'
                      "
                      dense
                      outlined
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn
                      @click="searchRnc"
                      color="primary"
                      block
                      v-if="selectedDocument && selectedDocument.value == 1 && !editedItem.entity_id"
                    >
                      <v-icon size="22px" left>search</v-icon>
                      Consultar
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col cols="12" md="8">
                    <v-text-field
                      :disabled="disabledName"
                      dense
                      outlined
                      v-model="editedItem.name"
                      label="Nombre/Razón social"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mt-1" align="center">
                  <v-col cols="12" md="8">
                    <v-autocomplete
                      dense
                      outlined
                      v-model="selected_business_unit"
                      :items="business_units"
                      item-text="description_long"
                      item-value="id"
                      @change="setLocationsBusinessUnit"
                      color="cyanText"
                      :readonly="$store.state.auth.user.user.permition_level == 111"
                      label="Unidad de negocio - opcional"
                      hide-details="auto"
                      required
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="1">
                    <v-icon
                      v-if="selected_business_unit"
                      @click="setLocationsBusinessUnit"
                      color="primary"
                      size="30"
                      >refresh</v-icon
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-card>

            <!-- Sucursales -->
            <v-card class="rounded-lg mb-4" outlined>
              <v-card-title class="cs-section-title">
                <v-icon left color="primary">mdi-office-building-marker-outline</v-icon>
                Sucursales
              </v-card-title>
              <v-divider></v-divider>
              <v-container class="px-5 py-4">
                <v-row dense align="center">
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-model="descriptionSucursal"
                      dense
                      outlined
                      hide-details
                      label="Nombre sucursal"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-model="addressSucursal"
                      dense
                      outlined
                      hide-details
                      label="Dirección sucursal"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn @click="addSucursal" small color="primary" outlined block>
                      <v-icon left>add</v-icon> Agregar
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row no-gutters dense>
                  <v-col cols="12">
                    <v-data-table
                      :items-per-page="20"
                      :headers="headersSucursal"
                      :items="itemsSucursal"
                      hide-default-footer
                      class="elevation-0"
                      no-data-text="Sin sucursales agregadas"
                    >
                      <template v-slot:[`item.actions`]="{ item }">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="error"
                              class="mr-2"
                              v-bind="attrs"
                              v-on="on"
                              @click="deleteItemSucursal(item)"
                            >
                              close
                            </v-icon>
                          </template>
                          <span>Eliminar</span>
                        </v-tooltip>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>

            <!-- Direcciones / contactos -->
            <v-card class="rounded-lg mb-4" outlined>
              <v-card-title class="cs-section-title">
                <v-icon left color="primary">mdi-contacts-outline</v-icon>
                Direcciones / contactos
              </v-card-title>
              <v-divider></v-divider>
              <v-container class="px-5 py-4">
                <v-row dense align="center">
                  <v-col cols="12" sm="4" md="3">
                    <v-autocomplete
                      dense
                      outlined
                      hide-details
                      v-model="selectedTypes"
                      :items="typeContacts"
                      return-object
                      item-value="id"
                      item-text="description"
                      color="cyanText"
                      label="Tipo"
                      required
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" md="7">
                    <v-text-field
                      v-model="typeDescription"
                      dense
                      outlined
                      hide-details
                      :label="selectedTypes != null ? selectedTypes.description : 'Descripción'"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="2" md="2">
                    <v-btn @click="addContacts" small color="primary" outlined block>
                      <v-icon left>add</v-icon> Agregar
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row no-gutters dense>
                  <v-col cols="12">
                    <v-data-table
                      :headers="headersContacts"
                      :items="itemContacts"
                      hide-default-footer
                      class="elevation-0"
                      no-data-text="Sin contactos agregados"
                    >
                      <template v-slot:[`item.actions`]="{ item }">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="error"
                              class="mr-2"
                              v-bind="attrs"
                              v-on="on"
                              @click="deleteItemContact(item)"
                            >
                              close
                            </v-icon>
                          </template>
                          <span>Eliminar</span>
                        </v-tooltip>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>

            <!-- Cuentas de bancos -->
            <v-card class="rounded-lg mb-4" outlined>
              <v-card-title class="cs-section-title">
                <v-icon left color="primary">mdi-bank-outline</v-icon>
                Cuentas de bancos
              </v-card-title>
              <v-divider></v-divider>
              <v-container class="px-5 py-4">
                <v-row dense align="center">
                  <v-col cols="12" md="3">
                    <v-autocomplete
                      :items="itemsBanks"
                      v-model="newAccount.account_bank_id"
                      dense
                      outlined
                      hide-details
                      item-text="bank_description_long"
                      item-value="bank_id"
                      label="Banco"
                      required
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-autocomplete
                      :items="itemsAccountypes"
                      v-model="newAccount.account_type_id"
                      dense
                      outlined
                      hide-details
                      item-text="description"
                      item-value="id"
                      label="Tipo de cuenta"
                      required
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      v-model="newAccount.check_digit"
                      label="Número cuenta"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      v-model="newAccount.account_name"
                      label="Responsable"
                      :placeholder="editedItem.name"
                      persistent-placeholder
                      prepend-inner-icon="mdi-account-outline"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="1">
                    <v-btn @click="addAccount" small color="primary" outlined fab>
                      <v-icon>add</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row no-gutters dense>
                  <v-col cols="12">
                    <v-data-table
                      :headers="headersAccount"
                      :items="bussiness_account"
                      hide-default-footer
                      class="elevation-0"
                      no-data-text="Sin cuentas agregadas"
                    >
                      <template v-slot:[`item.actions`]="{ item }">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="error"
                              class="mr-2"
                              v-bind="attrs"
                              v-on="on"
                              @click="deleteItemAccount(item)"
                            >
                              close
                            </v-icon>
                          </template>
                          <span>Eliminar</span>
                        </v-tooltip>
                      </template>
                      <template v-slot:[`item.is_default`]="{ item }">
                        <v-checkbox
                          @change="handleDefaultChange(item)"
                          v-model="item.is_default"
                          hide-details
                          class="ma-0 pa-0"
                        ></v-checkbox>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>

            <!-- Representantes (entrega de cheques sin cuenta bancaria) -->
            <v-card class="rounded-lg mb-4" outlined>
              <v-card-title class="cs-section-title">
                <v-icon left color="primary">mdi-account-cash-outline</v-icon>
                Representantes
              </v-card-title>
              <v-divider></v-divider>
              <v-container class="px-5 py-4">
                <div class="text-caption grey--text mb-2">
                  Personas a las que se les entrega el cheque cuando no hay cuenta bancaria. Solo escribe el nombre.
                </div>
                <v-row dense align="center">
                  <v-col cols="12" md="10">
                    <v-text-field
                      v-model="newRepresentative.name"
                      dense
                      outlined
                      hide-details
                      label="Nombre del representante"
                      prepend-inner-icon="mdi-account-outline"
                      @keyup.enter="addRepresentative"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn @click="addRepresentative" small color="primary" outlined block>
                      <v-icon left>add</v-icon> Agregar
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row no-gutters dense>
                  <v-col cols="12">
                    <v-data-table
                      :headers="headersRepresentatives"
                      :items="representatives"
                      hide-default-footer
                      class="elevation-0"
                      no-data-text="Sin representantes agregados"
                    >
                      <template v-slot:[`item.actions`]="{ item }">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="error"
                              class="mr-2"
                              v-bind="attrs"
                              v-on="on"
                              @click="deleteRepresentative(item)"
                            >
                              close
                            </v-icon>
                          </template>
                          <span>Eliminar</span>
                        </v-tooltip>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>

          <!-- ===================== RIGHT COLUMN ===================== -->
          <v-col cols="12" md="4">
            <v-card class="rounded-lg mb-4" outlined>
              <v-card-title class="cs-section-title">
                <v-icon left color="primary">mdi-account-switch-outline</v-icon>
                Roles y condiciones
              </v-card-title>
              <v-divider></v-divider>
              <v-container class="px-4 py-4">
                <!-- Role selector -->
                <v-card class="rounded-lg mb-4" outlined>
                  <v-row justify="center" no-gutters>
                    <v-col cols="6" class="d-flex justify-center">
                      <v-checkbox
                        v-if="type == 2 || type == 1"
                        v-model="cliente"
                        label="Cliente"
                        color="primary"
                        hide-details
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-center">
                      <v-checkbox
                        v-model="suplidor"
                        v-if="type == 3 || type == 1"
                        label="Suplidor"
                        color="primary"
                        hide-details
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-card>

                <!-- Currency -->
                <div class="text-center text-caption grey--text font-weight-medium mb-1">
                  Moneda por defecto
                </div>
                <div class="d-flex justify-center mb-4">
                  <v-btn-toggle
                    v-model="seleted_currencies"
                    class="rounded-lg"
                    mandatory
                    color="primary"
                  >
                    <v-btn v-for="(item, i) of currencies" :key="i" :value="item.id" small>
                      {{ item.code }}
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <!-- Cliente block -->
                <template v-if="cliente">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-subtitle-2 font-weight-bold blue--text text--darken-1 mb-2">
                    <v-icon small left color="blue darken-1">mdi-account-tie</v-icon>Cliente
                  </div>
                  <v-autocomplete
                    v-show="selected_business_unit == null"
                    :disabled="editedItem.entity_id != null && originalCustomerTypeId != null"
                    dense
                    outlined
                    v-model="customer_type_id"
                    :items="customer_types"
                    color="cyanText"
                    label="Tipo de cliente"
                    item-value="id"
                    item-text="name"
                    required
                  ></v-autocomplete>
                  <v-autocomplete
                    dense
                    outlined
                    v-model="selectedtypesNcfCliente"
                    :items="ncfTypes"
                    color="cyanText"
                    label="Tipo de NCF Cliente"
                    required
                  ></v-autocomplete>
                  <v-autocomplete
                    dense
                    outlined
                    v-model="selectedCreditDaysCliente"
                    :items="credit_days"
                    color="cyanText"
                    label="Plazo de pago cliente"
                    required
                  ></v-autocomplete>
                </template>

                <!-- Suplidor block -->
                <template v-if="suplidor">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-subtitle-2 font-weight-bold purple--text text--darken-1 mb-2">
                    <v-icon small left color="purple darken-1">mdi-truck-delivery</v-icon>Suplidor
                  </div>
                  <v-autocomplete
                    v-show="selected_business_unit == null"
                    dense
                    outlined
                    v-model="supplier_type_id"
                    :items="supplier_types"
                    color="cyanText"
                    label="Tipo de suplidor"
                    item-value="id"
                    item-text="name"
                    required
                    :disabled="editedItem.entity_id != null && originalSupplierTypeId != null"
                  ></v-autocomplete>
                  <v-autocomplete
                    dense
                    outlined
                    v-model="selectedtypesNcfSuplidor"
                    :items="ncfTypes.filter((item) => [7, 5].includes(item.value))"
                    color="cyanText"
                    label="Tipo de NCF Suplidor"
                    required
                  ></v-autocomplete>
                  <v-autocomplete
                    dense
                    outlined
                    v-model="selectedCreditDaysSuplidor"
                    :items="credit_days"
                    color="cyanText"
                    label="Plazo de pago suplidor"
                    required
                  ></v-autocomplete>
                  <v-autocomplete
                    dense
                    outlined
                    v-model="selectedWithholdingsSuplidor"
                    item-text="name"
                    item-value="id"
                    :items="withholdings"
                    color="cyanText"
                    label="Retenciones Suplidor"
                    required
                  ></v-autocomplete>
                  <v-autocomplete
                    dense
                    outlined
                    multiple
                    v-model="selected_payment_methods"
                    item-text="name"
                    item-value="id"
                    :items="payments_methods"
                    color="cyanText"
                    label="Metodos de pagos"
                    required
                  ></v-autocomplete>
                </template>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-col>

    <!-- ============================================================= -->
    <!-- ===================== STATUS DIALOG ========================= -->
    <!-- ============================================================= -->
    <v-dialog v-model="dialogStatus" max-width="480px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="text-h6 font-weight-bold primary white--text pa-4">
          <v-icon left>{{ editedItem.status_id ? 'mdi-account-off' : 'mdi-account-check' }}</v-icon>
          {{ editedItem.status_id ? 'Inactivar' : 'Activar' }} Registro
        </v-card-title>
        <v-card-text class="pa-6 text-center">
          <div class="text-h6 grey--text text--darken-2">
            ¿Seguro que desea {{ editedItem.status_id ? 'inactivar' : 'activar' }} a
            <span class="font-weight-black">{{ editedItem.name }}</span>?
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text rounded @click="dialogStatus=false">Cancelar</v-btn>
          <v-btn :color="editedItem.status_id ? 'orange darken-1' : 'success'" depressed rounded class="white--text px-6" @click="saveStatus">
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===================== COMPARAR DUPLICADOS (full screen) ============ -->
    <v-dialog
      v-model="compareDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="cs-compare-bg">
        <v-toolbar flat color="primary" dark>
          <v-btn icon @click="compareDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-icon left>mdi-compare-horizontal</v-icon>
          <v-toolbar-title class="font-weight-bold">Comparar entidades</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip v-if="compareMeta.score" label color="white" text-color="primary" class="font-weight-bold mr-2">
            {{ compareMeta.score }}% coincidencia
          </v-chip>
          <v-chip v-if="compareMeta.match_by" label outlined>
            Por {{ compareMeta.match_by }}
          </v-chip>
        </v-toolbar>

        <div class="pa-4 pa-md-6" style="max-width: 1200px; margin: 0 auto; width: 100%">
          <!-- Encabezados de las dos entidades -->
          <v-row dense>
            <v-col v-for="side in ['a', 'b']" :key="'head-' + side" cols="12" md="6">
              <v-card
                outlined
                class="rounded-lg pa-4 d-flex align-center"
                :class="side === 'a' ? 'cs-side-a' : 'cs-side-b'"
              >
                <v-avatar :color="side === 'a' ? 'blue lighten-4' : 'purple lighten-4'" size="48" class="mr-3">
                  <span
                    class="font-weight-bold text-h6"
                    :class="side === 'a' ? 'blue--text text--darken-2' : 'purple--text text--darken-2'"
                  >
                    {{ ((side === 'a' ? compareA.name : compareB.name) || ' ').charAt(0) }}
                  </span>
                </v-avatar>
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3 text-truncate">
                    {{ (side === 'a' ? compareA.name : compareB.name) || ('Entidad ' + side.toUpperCase()) }}
                  </div>
                  <div class="text-caption grey--text">
                    ID #{{ side === 'a' ? compareA.entity_id : compareB.entity_id }}
                    <span v-if="(side === 'a' ? compareA.document_id : compareB.document_id)">
                      · {{ side === 'a' ? compareA.document_id : compareB.document_id }}
                    </span>
                  </div>
                </div>
                <v-chip
                  small label
                  :color="(side === 'a' ? compareA.status_id : compareB.status_id) ? 'success lighten-4' : 'red lighten-4'"
                  :text-color="(side === 'a' ? compareA.status_id : compareB.status_id) ? 'success darken-2' : 'red darken-2'"
                >
                  {{ (side === 'a' ? compareA.status_id : compareB.status_id) ? 'Activo' : 'Inactivo' }}
                </v-chip>
              </v-card>
            </v-col>
          </v-row>

          <!-- Tabla comparativa -->
          <v-card outlined class="rounded-lg mt-4">
            <v-card-title class="cs-section-title py-3">
              <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
              Detalle
              <v-spacer></v-spacer>
              <span class="text-caption grey--text font-weight-regular">
                <v-icon x-small color="amber darken-3">mdi-square-rounded</v-icon>
                Diferencias resaltadas
              </span>
            </v-card-title>
            <v-divider></v-divider>
            <v-simple-table class="cs-compare-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th style="width: 22%"></th>
                    <th class="text-left cs-col-a">Entidad A</th>
                    <th class="text-left cs-col-b">Entidad B</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="group in compareGroups">
                    <tr :key="'g-' + group.title" class="cs-compare-group">
                      <td colspan="3">
                        <v-icon small left :color="group.color || 'primary'">{{ group.icon }}</v-icon>
                        <span class="font-weight-bold text-caption text-uppercase grey--text text--darken-2">
                          {{ group.title }}
                        </span>
                      </td>
                    </tr>
                    <tr
                      v-for="row in group.rows"
                      :key="group.title + '-' + row.label"
                      :class="{ 'cs-compare-diff': row.diff }"
                    >
                      <td class="text-caption font-weight-medium grey--text text--darken-1">
                        {{ row.label }}
                      </td>
                      <td class="cs-col-a">
                        {{ row.a }}
                        <v-icon v-if="row.diff" x-small color="amber darken-3" class="ml-1">mdi-not-equal-variant</v-icon>
                      </td>
                      <td class="cs-col-b">{{ row.b }}</td>
                    </tr>
                  </template>
                  <tr v-if="compareCountsLoading" class="cs-compare-group">
                    <td colspan="3">
                      <v-icon small left color="teal">mdi-file-document-multiple-outline</v-icon>
                      <span class="font-weight-bold text-caption text-uppercase grey--text text--darken-2">
                        Documentos relacionados
                      </span>
                      <v-progress-circular indeterminate size="14" width="2" color="teal" class="ml-2"></v-progress-circular>
                      <span class="text-caption grey--text ml-2">contando documentos...</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Asientos relacionados (libro diario por entidad) -->
          <v-card outlined class="rounded-lg mt-4">
            <v-card-title class="cs-section-title py-3">
              <v-icon left color="primary">mdi-notebook-outline</v-icon>
              Asientos relacionados
            </v-card-title>
            <v-divider></v-divider>
            <v-tabs v-model="dbTab" color="primary" grow>
              <v-tab>
                <v-icon left small color="blue darken-2">mdi-alpha-a-circle</v-icon>
                {{ compareA.name || "Entidad A" }}
                <v-chip x-small label class="ml-2" color="blue lighten-5" text-color="blue darken-2">
                  {{ dbA.total }}
                </v-chip>
              </v-tab>
              <v-tab>
                <v-icon left small color="purple darken-2">mdi-alpha-b-circle</v-icon>
                {{ compareB.name || "Entidad B" }}
                <v-chip x-small label class="ml-2" color="purple lighten-5" text-color="purple darken-2">
                  {{ dbB.total }}
                </v-chip>
              </v-tab>
            </v-tabs>
            <v-divider></v-divider>
            <v-tabs-items v-model="dbTab">
              <v-tab-item v-for="side in ['a', 'b']" :key="'db-' + side">
                <v-data-table
                  :headers="dbHeaders"
                  :items="(side === 'a' ? dbA : dbB).items"
                  :server-items-length="(side === 'a' ? dbA : dbB).total"
                  :loading="(side === 'a' ? dbA : dbB).loading"
                  :page.sync="(side === 'a' ? dbA : dbB).page"
                  :items-per-page="dbItemsPerPage"
                  :footer-props="{ 'items-per-page-options': [dbItemsPerPage] }"
                  loading-text="Cargando asientos..."
                  no-data-text="Sin asientos para esta entidad."
                  class="elevation-0"
                  @update:page="loadEntityDiaryBook(side)"
                >
                  <template v-slot:[`item.diary_book_code`]="{ item }">
                    <a class="primary--text font-weight-medium" style="cursor: pointer" @click="openEntry(item.diary_book_id)">
                      {{ item.diary_book_code }}
                    </a>
                  </template>
                  <template v-slot:[`item.department_name`]="{ item }">
                    {{ item.department_name }}
                  </template>
                  <template v-slot:[`item.debit`]="{ item }">
                    <span v-if="item.entry_type_id == 1" class="green--text text--darken-1">
                      {{ item.currency_code }} {{ currencyFormat(item.amount) }}
                    </span>
                  </template>
                  <template v-slot:[`item.credit`]="{ item }">
                    <span v-if="item.entry_type_id == 2" class="red--text text--darken-1">
                      {{ item.currency_code }} {{ currencyFormat(item.amount) }}
                    </span>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card>

          <!-- Fusionar entidades -->
          <v-card outlined class="rounded-lg mt-4">
            <v-card-title class="cs-section-title py-3">
              <v-icon left color="deep-orange darken-1">mdi-merge</v-icon>
              Fusionar entidades
            </v-card-title>
            <v-divider></v-divider>
            <div class="pa-4">
              <div class="text-body-2 grey--text text--darken-2 mb-2">
                La entidad que <strong>no</strong> se conserve será <strong>desactivada</strong> y todos
                sus documentos (facturas, notas, pagos, anticipos, órdenes, cotizaciones) se moverán a la
                conservada, recalculando la cuenta contable CxC/CxP correcta.
              </div>
              <v-radio-group v-model="mergeKeep" row hide-details class="mt-0" @change="mergePreview = null">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium mr-2">Conservar:</span>
                </template>
                <v-radio :value="'a'">
                  <template v-slot:label>
                    <span class="blue--text text--darken-2 font-weight-medium">{{ compareA.name }} (A)</span>
                  </template>
                </v-radio>
                <v-radio :value="'b'">
                  <template v-slot:label>
                    <span class="purple--text text--darken-2 font-weight-medium">{{ compareB.name }} (B)</span>
                  </template>
                </v-radio>
              </v-radio-group>

              <div class="d-flex mt-4" style="gap: 8px">
                <v-btn
                  color="primary"
                  outlined
                  :loading="mergeLoading && !confirmMergeDialog"
                  :disabled="!mergeKeep"
                  @click="previewMerge"
                >
                  <v-icon left small>mdi-eye-outline</v-icon>
                  Vista previa
                </v-btn>
                <v-btn
                  color="deep-orange darken-1"
                  depressed
                  dark
                  :disabled="!mergePreview || mergeLoading"
                  @click="confirmMergeDialog = true"
                >
                  <v-icon left small>mdi-merge</v-icon>
                  Confirmar fusión
                </v-btn>
              </div>

              <v-alert v-if="mergePreview" type="info" text dense border="left" class="mt-4 mb-2">
                Se moverá lo siguiente de
                <strong>{{ mergeRemoveEntity && mergeRemoveEntity.name }}</strong>
                hacia <strong>{{ mergeKeepEntity && mergeKeepEntity.name }}</strong>, y la primera quedará inactiva:
              </v-alert>
              <v-simple-table v-if="mergePreview" dense class="cs-compare-table">
                <template v-slot:default>
                  <tbody>
                    <tr v-for="row in mergePreviewRows" :key="row.label">
                      <td class="text-body-2">{{ row.label }}</td>
                      <td class="text-right font-weight-bold" style="width: 120px">{{ row.value }}</td>
                    </tr>
                    <tr v-if="mergePreviewRows.length === 0">
                      <td class="text-body-2 grey--text">La entidad seleccionada no tiene documentos que mover.</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card>

          <div class="d-flex justify-end mt-4">
            <v-btn text color="grey darken-1" @click="compareDialog = false">Cerrar</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- ===================== CONFIRMAR FUSIÓN ===================== -->
    <v-dialog v-model="confirmMergeDialog" max-width="520px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="text-h6 font-weight-bold deep-orange--text text--darken-1 pa-4">
          <v-icon left color="deep-orange darken-1">mdi-alert</v-icon>
          Confirmar fusión
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4 text-body-2">
          <p class="mb-2">
            Vas a mover todos los documentos de
            <strong class="red--text text--darken-1">{{ mergeRemoveEntity && mergeRemoveEntity.name }}</strong>
            (ID #{{ mergeRemoveEntity && mergeRemoveEntity.entity_id }}) hacia
            <strong class="green--text text--darken-2">{{ mergeKeepEntity && mergeKeepEntity.name }}</strong>
            (ID #{{ mergeKeepEntity && mergeKeepEntity.entity_id }}).
          </p>
          <p class="mb-0">
            La entidad <strong>{{ mergeRemoveEntity && mergeRemoveEntity.name }}</strong> quedará
            <strong>desactivada</strong>. Esta acción reasigna contabilidad y no es fácilmente reversible.
            ¿Continuar?
          </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" :disabled="mergeLoading" @click="confirmMergeDialog = false">Cancelar</v-btn>
          <v-btn color="deep-orange darken-1" depressed dark :loading="mergeLoading" @click="executeMerge">
            <v-icon left small>mdi-merge</v-icon>
            Sí, fusionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
export default {
  data: () => ({
    search: "",
    searchTimer: null,
    searchProgressTimer: null,
    searchProgress: 0,
    searchWaiting: false,
    listTab: 0,
    duplicates: [],
    duplicatesLoaded: false,
    loadingDuplicates: false,
    compareDialog: false,
    compareLoadingId: null,
    compareA: {},
    compareB: {},
    compareMeta: {},
    compareCountsA: null,
    compareCountsB: null,
    compareLedgerA: [],
    compareLedgerB: [],
    compareCountsLoading: false,
    dbTab: 0,
    dbItemsPerPage: 10,
    dbA: { items: [], total: 0, page: 1, loading: false, loaded: false },
    dbB: { items: [], total: 0, page: 1, loading: false, loaded: false },
    mergeKeep: null,
    mergeLoading: false,
    mergePreview: null,
    confirmMergeDialog: false,
    duplicatesHeaders: [
      { text: "Entidad A", value: "entity_a", sortable: false },
      { text: "Entidad B", value: "entity_b", sortable: false },
      { text: "Coincidencia", value: "score" },
      { text: "Por", value: "match_by", sortable: false },
      { text: "Acciones", value: "actions", sortable: false },
    ],
    dialog: false,
    dialogStatus: false,
    dialogDelete: false,
    suplidor: false,
    cliente: false,
    loading: false,
    disabledName: false,
    plazoPagoSuplidor: null,
    plazoPagoCliente: null,
    documents: [],
    itemsAccountypes: [],
    rncValid: false,
    typeDescription: "",
    descriptionSucursal: "",
    ncfTypes: [],
    accounts: [],
    customer_types: [],
    supplier_types: [],
    provincies: [],
    credit_days: [],
    withholdings: [],
    typeContacts: [],
    itemContacts: [],
    itemsSucursal: [],
    itemsBanks: [],
    currencies: [],
    supplier_type_id: null,
    customer_type_id: null,
    // Tipo de cliente/suplidor tal como vino de la BD; se usa para bloquear la
    // edición solo cuando ya estaba guardado (no cuando se acaba de elegir).
    originalCustomerTypeId: null,
    originalSupplierTypeId: null,
    payments_methods: [],
    selected_payment_methods: null,
    seleted_currencies: null,
    addressSucursal: "",
    cxc: "",
    cxp: "",
    selectedDocument: "",
    selected_business_unit: null,
    selectedWithholdingsCliente: null,
    selectedWithholdingsSuplidor: null,
    selectedtypesNcfCliente: null,
    entityData: [],
    selectedtypesNcfSuplidor: null,
    selectedCreditDaysCliente: null,
    selectedCreditDaysSuplidor: null,
    selectedProvincies: null,
    selectedTypes: null,
    bussiness_account: [],
    // Representantes: se guardan en la misma tabla de cuentas de banco con
    // bank_id=22 (banco ficticio/centinela), account_type_id=1, is_default=false
    // y account_name = nombre del representante. El número de cuenta se genera con
    // el entity_id.
    representatives: [],
    newRepresentative: { name: "" },
    repBankId: 22,
    headersRepresentatives: [
      { text: "Representante", value: "name" },
      { text: "Actions", value: "actions", sortable: false, width: "10%" },
    ],
    business_units: [],
    newAccount: {
      id: "",
      account_type_id: "",
      account_type_description: "",
      account_bank_id: "",
      bank_description_short: "",
      bank_description_long: "",
      account_name: "",
      currencie_id: "",
      check_digit: "",
      status_id: "",
    },
    headersSucursal: [
      { text: "Location", value: "location_id" },
      { text: "Descripción", value: "name" },
      { text: "Dirección", value: "address" },

      { text: "Actions", value: "actions", sortable: false, width: "10%" },
    ],
    headersContacts: [
      {
        text: "Tipo",
        align: "start",
        sortable: false,
        value: "description_type",
        width: "20%",
      },
      { text: "Descripcion", value: "description" },
      { text: "Actions", value: "actions", sortable: false, width: "10%" },
    ],
    headersAccount: [
      {
        text: "Banco",
        align: "start",
        sortable: false,
        value: "bank_description_long",
      },
      { text: "Tipo de cuenta", value: "account_type_description" },
      { text: "Número", value: "account_check_digit" },
      { text: "Responsable", value: "account_name" },
      {
        text: "Por defecto",
        value: "is_default",
        sortable: false,
        width: "10%",
      },
      { text: "Actions", value: "actions", sortable: false, width: "10%" },
    ],
    headers: [
      {
        text: "id",
        align: "start",
        sortable: false,
        value: "entity_id",
      },
      {
        text: "Nombre/Razón social",
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: "Identificación", value: "document_id" },
      { text: "Status", value: "status_id" },
      { text: "Rol", value: "role", sortable: false },
      { text: "Actions", value: "actions", sortable: false },
    ],

    editedIndex: false,
    editedItem: {},
    serverKpis: null,
    kpisLoading: false,
    filters: {
      status: "all",
      role: "all",
    },
    statusOptions: [
      { text: "Todos los Estados", value: "all" },
      { text: "Activos", value: true },
      { text: "Inactivos", value: false },
    ],
    roleOptions: [
      { text: "Todos los Roles", value: "all" },
      { text: "Clientes", value: "customer" },
      { text: "Suplidores", value: "supplier" },
    ],
  }),

  computed: {
    stats() {
      const k = this.serverKpis;
      const num = (v, fallback) => (k ? parseInt(v) || 0 : fallback);
      return {
        total: num(k && k.total_entities, this.entityData.length),
        customers: num(k && k.total_customers, this.entityData.filter((e) => e.is_customer).length),
        suppliers: num(k && k.total_suppliers, this.entityData.filter((e) => e.is_supplier).length),
        active: num(k && k.total_active, this.entityData.filter((e) => e.status_id).length),
        inactive: num(k && k.total_inactive, this.entityData.filter((e) => !e.status_id).length),
      };
    },
    formTitle() {
      return this.editedItem && this.editedItem.entity_id
        ? "Editar contacto"
        : "Nuevo contacto";
    },
    // Acceso al reporte/comparador de duplicados por permiso.
    canViewDuplicates() {
      return this.$store.state.auth.globalFeatures.includes("CNN-015");
    },
    mergeKeepEntity() {
      return this.mergeKeep === "a" ? this.compareA : this.mergeKeep === "b" ? this.compareB : null;
    },
    mergeRemoveEntity() {
      return this.mergeKeep === "a" ? this.compareB : this.mergeKeep === "b" ? this.compareA : null;
    },
    mergePreviewRows() {
      const r = this.mergePreview;
      if (!r) return [];
      const defs = [
        { key: "invoices_moved", label: "Facturas movidas" },
        { key: "responsable_moved", label: "Facturas (como responsable)" },
        { key: "invoice_lines_remapped", label: "Asientos de factura: cuenta CxC/CxP reasignada" },
        { key: "invoice_lines_reassigned", label: "Asientos de factura: líneas reasignadas de entidad" },
        { key: "note_lines_remapped", label: "Asientos de notas: cuenta CxC/CxP reasignada" },
        { key: "payments_reposted", label: "Pagos re-contabilizados" },
        { key: "entity_payments_moved", label: "Anticipos movidos" },
        { key: "prepayments_moved", label: "Prepagos movidos" },
        { key: "prepayment_lines_remapped", label: "Asientos de prepago: cuenta reasignada" },
        { key: "orders_moved", label: "Órdenes de compra movidas" },
        { key: "quotes_moved", label: "Cotizaciones movidas" },
      ];
      return defs
        .map((d) => ({ label: d.label, value: r[d.key] || 0 }))
        .filter((x) => x.value > 0);
    },
    dbHeaders() {
      return [
        { text: "Asiento", value: "diary_book_code", sortable: false },
        { text: "Tipo de Asiento", value: "diary_book_type_name", sortable: false },
        { text: "Ubicación", value: "location_description", sortable: false },
        { text: "Fecha", value: "formatted_effective_date", sortable: false },
        { text: "Cuenta Contable", value: "chart_of_account_name", sortable: false },
        { text: "Departamento", value: "department_name", sortable: false },
        { text: "Débito", value: "debit", align: "right", sortable: false },
        { text: "Crédito", value: "credit", align: "right", sortable: false },
      ];
    },
    compareGroups() {
      const a = this.compareA;
      const b = this.compareB;
      const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);

      const docType = (e) => {
        const d = this.documents.find((x) => x.value === e.document_id_type_id);
        return d ? d.name : "—";
      };
      const currency = (e) => {
        const c = this.currencies.find((x) => x.id == e.currency_id);
        return c ? c.code : "—";
      };
      const businessUnit = (e) => {
        const u = this.business_units.find(
          (x) => (x.id || x.bus_id) == e.business_unit_id
        );
        return u ? u.description_long : "—";
      };
      const roles = (e) => {
        const r = [];
        if (e.customer_details) r.push("Cliente");
        if (e.supplier_details) r.push("Suplidor");
        return r.length ? r.join(", ") : "—";
      };
      const accountName = (id) => {
        if (!id) return "—";
        const acc = this.accounts.find((x) => x.value == id);
        return acc ? acc.text : "#" + id;
      };
      const customer = (e) => (e.customer_details && e.customer_details[0] ? e.customer_details[0].customer : null);
      const supplier = (e) => (e.supplier_details && e.supplier_details[0] ? e.supplier_details[0].supplier : null);
      const custType = (e) => {
        const c = customer(e);
        if (!c) return "—";
        const t = this.customer_types.find((x) => x.id == c.customer_type_id);
        return t ? t.name : "—";
      };
      const supType = (e) => {
        const s = supplier(e);
        if (!s) return "—";
        const t = this.supplier_types.find((x) => x.id == s.supplier_type_id);
        return t ? t.name : "—";
      };
      const cxc = (e) => {
        const c = customer(e);
        return c ? accountName(c.sale_chart_of_account_id) : "—";
      };
      const cxp = (e) => {
        const s = supplier(e);
        return s ? accountName(s.purchase_chart_of_account_id) : "—";
      };

      const buildRows = (defs) =>
        defs.map((d) => {
          const va = d.get(a);
          const vb = d.get(b);
          return { label: d.label, a: va, b: vb, diff: String(va) !== String(vb) };
        });

      const groups = [
        {
          title: "General",
          icon: "mdi-card-account-details-outline",
          rows: buildRows([
            { label: "Nombre / Razón social", get: (e) => val(e.name) },
            { label: "Documento", get: (e) => val(e.document_id) },
            { label: "Tipo de identificación", get: docType },
            { label: "Roles", get: roles },
            { label: "Moneda", get: currency },
            { label: "Unidad de negocio", get: businessUnit },
            { label: "Estado", get: (e) => (e.status_id ? "Activo" : "Inactivo") },
            { label: "Sucursales", get: (e) => (e.entity_branches || []).length },
            { label: "Contactos", get: (e) => (e.entity_contact_json || []).length },
            { label: "Entity ID (interno)", get: (e) => val(e.entity_id) },
          ]),
        },
      ];

      if (customer(a) || customer(b)) {
        groups.push({
          title: "Cliente",
          icon: "mdi-account-tie",
          color: "blue",
          rows: buildRows([
            { label: "Tipo de cliente", get: custType },
            { label: "Cuenta por cobrar (CxC)", get: cxc },
          ]),
        });
      }

      if (supplier(a) || supplier(b)) {
        groups.push({
          title: "Suplidor",
          icon: "mdi-truck-delivery",
          color: "purple",
          rows: buildRows([
            { label: "Tipo de suplidor", get: supType },
            { label: "Cuenta por pagar (CxP)", get: cxp },
          ]),
        });
      }

      // Documentos relacionados (conteos desde backend)
      const ca = this.compareCountsA;
      const cb = this.compareCountsB;
      if (ca || cb) {
        const countLabels = [
          { key: "sales_invoices", label: "Facturas de venta" },
          { key: "purchase_invoices", label: "Facturas de compra" },
          { key: "conduces", label: "Conduces" },
          { key: "quotes", label: "Cotizaciones" },
          { key: "purchase_orders", label: "Órdenes de compra" },
          { key: "receivable_payments", label: "Cobros (CxC)" },
          { key: "payable_payments", label: "Pagos (CxP)" },
          { key: "credit_notes", label: "Notas de crédito" },
          { key: "debit_notes", label: "Notas de débito" },
        ];
        const rows = countLabels
          .map((c) => {
            const va = ca ? ca[c.key] || 0 : 0;
            const vb = cb ? cb[c.key] || 0 : 0;
            return { label: c.label, a: va, b: vb, diff: String(va) !== String(vb), _n: va + vb };
          })
          .filter((r) => r._n > 0);
        groups.push({
          title: "Documentos relacionados",
          icon: "mdi-file-document-multiple-outline",
          color: "teal",
          rows: rows.length
            ? rows
            : [{ label: "Sin documentos", a: 0, b: 0, diff: false }],
        });
      }

      // Cuentas contables CxC/CxP que aparecen en los asientos (libro) de cada entidad
      const la = this.compareLedgerA || [];
      const lb = this.compareLedgerB || [];
      if (la.length || lb.length) {
        const byAcc = {};
        const add = (arr, side) => {
          arr.forEach((r) => {
            if (!byAcc[r.account_id]) {
              byAcc[r.account_id] = {
                name: r.account_name,
                kind: r.kind,
                a: 0,
                b: 0,
              };
            }
            byAcc[r.account_id][side] = r.lines;
          });
        };
        add(la, "a");
        add(lb, "b");
        const rows = Object.keys(byAcc).map((id) => {
          const x = byAcc[id];
          return {
            label: `${x.name}${x.kind ? " · " + x.kind : ""}`,
            a: x.a + " línea(s)",
            b: x.b + " línea(s)",
            diff: x.a !== x.b,
          };
        });
        groups.push({
          title: "Cuentas en el libro (CxC / CxP)",
          icon: "mdi-book-open-variant",
          color: "deep-orange",
          rows,
        });
      }

      return groups;
    },
    customerTypes() {
      const k = this.serverKpis;
      if (!k) return [];
      return [
        { name: "Externo", count: parseInt(k.type_externo) || 0 },
        { name: "Afiliado", count: parseInt(k.type_afiliado) || 0 },
        { name: "Relacionado", count: parseInt(k.type_relacionado) || 0 },
        { name: "Empleado", count: parseInt(k.type_empleado) || 0 },
      ];
    },
    supplierTypes() {
      const k = this.serverKpis;
      if (!k) return [];
      return [
        { name: "Externo", count: parseInt(k.sup_type_externo) || 0 },
        { name: "Afiliado", count: parseInt(k.sup_type_afiliado) || 0 },
        { name: "Relacionado", count: parseInt(k.sup_type_relacionado) || 0 },
        { name: "Empleado", count: parseInt(k.sup_type_empleado) || 0 },
      ];
    },
    filteredEntities() {
      return this.entityData.filter((item) => {
        const matchStatus =
          this.filters.status === "all" || item.status_id === this.filters.status;
        const matchRole =
          this.filters.role === "all" ||
          (this.filters.role === "customer" && item.is_customer) ||
          (this.filters.role === "supplier" && item.is_supplier);
        return matchStatus && matchRole;
      });
    },
  },
  props: {
    entitys: {
      type: Array,
      required: true,
    },
    type: {
      type: Number,
      required: true,
    },
  },
  watch: {
    search(val) {
      clearTimeout(this.searchTimer);
      clearInterval(this.searchProgressTimer);
      this.searchProgress = 0;
      if (val.length >= 2) {
        // Debounce: espera a que el usuario deje de escribir antes de consultar.
        // La barrita muestra el avance de esa espera (0 → 100% en DEBOUNCE_MS).
        const DEBOUNCE_MS = 400;
        this.searchWaiting = true;
        const startTime = Date.now();
        this.searchProgressTimer = setInterval(() => {
          const elapsed = Date.now() - startTime;
          this.searchProgress = Math.min(100, (elapsed / DEBOUNCE_MS) * 100);
          if (elapsed >= DEBOUNCE_MS) clearInterval(this.searchProgressTimer);
        }, 25);
        this.searchTimer = setTimeout(() => {
          this.searchProgress = 100;
          this.searchWaiting = false;
          this.searchClient(val);
        }, DEBOUNCE_MS);
      } else {
        this.searchWaiting = false;
        this.entityData = this.entitys;
      }
    },
    "editedItem.name"(val, oldVal) {
      // Por defecto, "Responsable" de la cuenta de banco toma el nombre del
      // contacto; si el usuario aún no lo personalizó, se mantiene sincronizado.
      if (!this.newAccount.account_name || this.newAccount.account_name === oldVal) {
        this.newAccount.account_name = val || "";
      }
    },
    "editedItem.document_id"(val, oldVal) {
      if (val != oldVal) {
        this.disabledName = false;
      }
    },

    dialog(val) {
      val || this.close();
    },

    dialogDelete(val) {
      val || this.closeDelete();
    },

    dbTab(val) {
      // Carga perezosa del libro de la entidad B al abrir su tab por primera vez.
      if (val === 1 && !this.dbB.loaded && !this.dbB.loading) {
        this.loadEntityDiaryBook("b");
      }
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    setLocationsBusinessUnit() {
      const locations = this.$store.state.auth.all_locations.filter(
        (element) => element.bus_id == this.selected_business_unit && element.status_id == 1
      );

      // Filtra solo las ubicaciones que no existen en itemsSucursal
      const newLocations = locations.filter(
        (location) => !this.itemsSucursal.some(
          (sucursal) => sucursal.location_id === location.id
        )
      );

      // Agrega las nuevas ubicaciones
      this.itemsSucursal = [
        ...this.itemsSucursal,
        ...newLocations.map(element => ({
          location_id: element.id,
          name: element.desc_loc_long,
          address: "",
        }))
      ];
    },
    async searchClient(val) {
      this.loading = true;
      this.entityData = [];
      let typ = "";
      typ = this.type === 1 ? "" : "";
      typ = this.type === 2 ? "AND IS_CUSTOMER=true" : "";
      typ = this.type === 3 ? "AND IS_SUPPLIER=true" : "";

      const responseRequest = await this.api_Post("accounting/search_entity", {
        name: val,
        type: typ,
        all:true
      });
      if (responseRequest.status == 200) {
        let data = await responseRequest.json();

        this.entityData = data;
      } else {
        this.$toast.error("¡Error al agregar!");
      }
      this.loading = false;
    },
    resetNewAccount() {
      this.newAccount = {
        id: "",
        account_type_id: "",
        account_type_description: "",
        account_bank_id: "",
        bank_description_short: "",
        bank_description_long: "",
        account_name: this.editedItem && this.editedItem.name ? this.editedItem.name : "",
        currencie_id: "",
        check_digit: "",
        status_id: "",
      };
    },
    responsableToSave(value) {
      // Si el Responsable es vacío o igual al nombre de la entidad, se envía null
      // (el backend lo guarda como NULL y el frontend lo rellena al consultar).
      const responsable = (value || "").trim();
      const entityName = (this.editedItem.name || "").trim();
      return responsable && responsable !== entityName ? responsable : null;
    },
    newItem() {
      this.close();
      this.$nextTick(() => {
        this.dialog = true;
      });
    },
    addSucursal() {
      if(this.descriptionSucursal===""){
        this.$toast.warning("Debes agregar una descripción!");
        return;
      }
      this.itemsSucursal.push({
        id:null,
        name: this.descriptionSucursal,
        address: this.addressSucursal,
      });
      this.descriptionSucursal = "";
      this.addressSucursal = "";
    },
    addContacts() {
      if (this.selectedTypes === null) {
        this.$toast.warning("Debes seleccionar un tipo!");
        return;
      }
      if (
        this.typeDescription === "" ||
        this.typeDescription === undefined ||
        this.typeDescription === null
      ) {
        this.$toast.warning(
          "Debes agregar una descripción a " + this.selectedTypes.description + " !"
        );
        return;
      }
      this.itemContacts.push({
        id_type: this.selectedTypes.id,
        description_type: this.selectedTypes.description,
        description: this.typeDescription,
      });
      this.selectedTypes = null;
      this.typeDescription = "";
    },
    addAccount() {
      if (this.newAccount.account_bank_id == "") {
        this.$toast.warning("Escoja el banco!");
        return;
      }
      if (this.newAccount.account_type_id == "") {
        this.$toast.warning("Indique el tipo de cuenta!");
        return;
      }

      let banc_text = this.itemsBanks.find(
        (element) => element.bank_id == this.newAccount.account_bank_id
      );
      let tipeAcon_text = this.itemsAccountypes.find(
        (element) => element.id == this.newAccount.account_type_id
      );

      if (
        parseInt(banc_text.bank_digits) != parseInt(this.newAccount.check_digit.length)
      ) {
        this.$toast.warning("La cantidad de digitos debe ser " + banc_text.bank_digits);
        return;
      }

      this.bussiness_account.push({
        account_type_id: this.newAccount.account_type_id,
        account_type_description: tipeAcon_text.description,
        account_bank_id: this.newAccount.account_bank_id,
        bank_description_long: banc_text.bank_description_long,
        account_check_digit: this.newAccount.check_digit,
        account_name: this.newAccount.account_name || (this.editedItem ? this.editedItem.name : ""),
        is_default: false,
      });

      this.resetNewAccount();
    },
    handleDefaultChange(selectedItem) {
      // Cambia todos los valores predeterminados (is_default) a falso
      this.bussiness_account.forEach((item) => {
        if (item !== selectedItem) {
          item.is_default = false;
        }
      });
    },
    deleteItemContact(item) {
      let index = this.itemContacts.indexOf(item);

      this.itemContacts.splice(index, 1);
    },
    deleteItemSucursal(item) {
      let index = this.itemsSucursal.indexOf(item);

      this.itemsSucursal.splice(index, 1);
    },
    deleteItemAccount(item) {
      let index = this.bussiness_account.indexOf(item);

      this.bussiness_account.splice(index, 1);
    },
    // Genera el número de cuenta interno del representante: entity_id + secuencia
    // rellenada con ceros hasta 10 dígitos (ej. entity_id=178, 4º -> 1780000004).
    // La secuencia = mayor sufijo existente + 1, para no colisionar tras eliminar.
    generateRepAccount(entityId) {
      const idStr = String(entityId);
      let maxSeq = 0;
      for (const rep of this.representatives) {
        const suf = parseInt(String(rep.account || "").slice(idStr.length), 10);
        if (!isNaN(suf) && suf > maxSeq) maxSeq = suf;
      }
      const seq = maxSeq + 1;
      const pad = Math.max(0, 10 - idStr.length);
      return idStr + String(seq).padStart(pad, "0");
    },
    addRepresentative() {
      const name = (this.newRepresentative.name || "").trim();
      if (!name) {
        this.$toast.warning("Escribe el nombre del representante!");
        return;
      }
      if (!this.editedItem || !this.editedItem.entity_id) {
        this.$toast.warning("Guarda el suplidor primero para poder agregar representantes.");
        return;
      }
      this.representatives.push({
        name: name,
        account: this.generateRepAccount(this.editedItem.entity_id),
      });
      this.newRepresentative.name = "";
    },
    deleteRepresentative(item) {
      const index = this.representatives.indexOf(item);
      this.representatives.splice(index, 1);
    },

    async initialize() {
      this.loading = true;

      try {
        const result = await Promise.all([
          this.api_Get("accounting/currencies"),
          this.api_Get("accounting/accountsUsable"),
          this.g_bussines(),
          this.g_Bancos(),
          this.g_type_account(),
          this.api_Get("income/payments_types"),
          this.api_Get("accounting/documents"),
          this.api_Get("entity/types_contacts"),
          this.api_Get("accounting/credit_days"),
          this.api_Get("accounting/withholdings"),
          this.api_Get("accounting/nfc_types"),
          this.api_Get("accounting/provincies"),
          this.api_Get("entity/entity_types")
        ]);

        this.currencies = await result[0].json();

        const dataTypeAccounts = await result[1].json();
        this.accounts = dataTypeAccounts.map(item => ({
          text: item.name,
          value: item.id,
          dgii_category_id: item.dgii_category_id,
        }));

        this.business_units = await result[2];
        this.itemsBanks = await result[3];
        this.itemsAccountypes = await result[4];
        this.payments_methods = await result[5].json();

        const documentsData = await result[6].json();
        this.documents = documentsData.map(element => ({
          name: element.name,
          value: element.id
        }));

        this.typeContacts = await result[7].json();

        const resultCreditDays = await result[8].json();
        this.credit_days = resultCreditDays.map(item => ({
          text: item.name,
          value: item.id
        }));

        this.withholdings = await result[9].json();

        const resultTypesNcf = await result[10].json();
        this.ncfTypes = resultTypesNcf.map(item => ({
          text: item.name,
          value: item.id
        }));

        const resultProvincies = await result[11].json();
        this.provincies = resultProvincies.map(item => ({
          text: item.name,
          value: item.id
        }));
        const entity_types=await result[12].json();
        this.customer_types = entity_types;

        this.supplier_types = entity_types;

        this.entityData = this.entitys;
        this.loadKpis();
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadKpis() {
      this.kpisLoading = true;
      try {
        const res = await this.api_Get(`entity/entity_kpis?type=${this.type}`);
        if (res.status === 200) {
          this.serverKpis = await res.json();
        }
      } catch (e) {
        console.error('Error loading KPIs:', e);
      } finally {
        this.kpisLoading = false;
      }
    },
    async loadDuplicates() {
      this.loadingDuplicates = true;
      try {
        const res = await this.api_Get("entity/entity_duplicates");
        if (res.status === 200) {
          this.duplicates = await res.json();
          this.duplicatesLoaded = true;
        } else {
          this.$toast.error("Error al generar el reporte de duplicados");
        }
      } catch (e) {
        console.error("Error loading duplicates:", e);
        this.$toast.error("Error al generar el reporte de duplicados");
      } finally {
        this.loadingDuplicates = false;
      }
    },
    async openCompare(item) {
      this.compareLoadingId = item.id_a + "-" + item.id_b;
      try {
        const [resA, resB] = await Promise.all([
          this.api_Post("accounting/search_entity_by_id", { id: item.id_a }),
          this.api_Post("accounting/search_entity_by_id", { id: item.id_b }),
        ]);
        const dataA = resA.status === 200 ? await resA.json() : [];
        const dataB = resB.status === 200 ? await resB.json() : [];
        if (!dataA.length || !dataB.length) {
          this.$toast.error("No se pudieron cargar ambas entidades");
          return;
        }
        this.compareA = dataA[0];
        this.compareB = dataB[0];
        this.compareMeta = { score: item.score, match_by: item.match_by };
        this.compareCountsA = null;
        this.compareCountsB = null;
        this.compareLedgerA = [];
        this.compareLedgerB = [];
        this.dbTab = 0;
        this.dbA = { items: [], total: 0, page: 1, loading: false, loaded: false };
        this.dbB = { items: [], total: 0, page: 1, loading: false, loaded: false };
        this.mergeKeep = null;
        this.mergePreview = null;
        this.compareDialog = true;
        this.loadCompareCounts(item.id_a, item.id_b);
        this.loadEntityDiaryBook("a");
      } catch (e) {
        console.error("Error comparing entities:", e);
        this.$toast.error("No se pudo comparar las entidades");
      } finally {
        this.compareLoadingId = null;
      }
    },
    async loadCompareCounts(idA, idB) {
      this.compareCountsLoading = true;
      try {
        const res = await this.api_Get(
          `entity/entity_related_counts?id_a=${idA}&id_b=${idB}`
        );
        if (res.status === 200) {
          const data = await res.json();
          this.compareCountsA = data.a || {};
          this.compareCountsB = data.b || {};
          this.compareLedgerA = data.ledgerA || [];
          this.compareLedgerB = data.ledgerB || [];
        }
      } catch (e) {
        console.error("Error loading related counts:", e);
      } finally {
        this.compareCountsLoading = false;
      }
    },
    async loadEntityDiaryBook(side) {
      const st = side === "a" ? this.dbA : this.dbB;
      const entity = side === "a" ? this.compareA.entity_id : this.compareB.entity_id;
      if (!entity) return;
      st.loading = true;
      try {
        const res = await this.api_Get(
          `entity/entity_diary_book?entity_id=${entity}&page=${st.page}&limit=${this.dbItemsPerPage}`
        );
        if (res.status === 200) {
          const data = await res.json();
          st.items = data.data || [];
          st.total = data.totalItems || 0;
          st.loaded = true;
        }
      } catch (e) {
        console.error("Error loading entity diary book:", e);
      } finally {
        st.loading = false;
      }
    },
    openEntry(diaryBookId) {
      const route = this.$router.resolve({
        name: "ViewEntryDetail",
        params: { id: diaryBookId },
      });
      window.open(route.href, "_blank");
    },
    async previewMerge() {
      if (!this.mergeKeepEntity || !this.mergeRemoveEntity) return;
      this.mergeLoading = true;
      this.mergePreview = null;
      try {
        const res = await this.api_Post("entity/merge_entities", {
          keep_id: this.mergeKeepEntity.entity_id,
          remove_id: this.mergeRemoveEntity.entity_id,
          dry_run: true,
        });
        if (res.status === 200) {
          const data = await res.json();
          this.mergePreview = data.report;
        } else {
          const err = await res.json().catch(() => ({}));
          this.$toast.error(err.error || "No se pudo generar la vista previa");
        }
      } catch (e) {
        console.error("Error preview merge:", e);
        this.$toast.error("No se pudo generar la vista previa de fusión");
      } finally {
        this.mergeLoading = false;
      }
    },
    async executeMerge() {
      if (!this.mergeKeepEntity || !this.mergeRemoveEntity) return;
      this.mergeLoading = true;
      try {
        const removeId = this.mergeRemoveEntity.entity_id;
        const res = await this.api_Post("entity/merge_entities", {
          keep_id: this.mergeKeepEntity.entity_id,
          remove_id: removeId,
          dry_run: false,
        });
        if (res.status === 200) {
          this.$toast.success("Entidades fusionadas con éxito");
          this.confirmMergeDialog = false;
          this.compareDialog = false;
          // Quitar del reporte los pares que involucran la entidad eliminada
          this.duplicates = this.duplicates.filter(
            (d) => d.id_a !== removeId && d.id_b !== removeId
          );
          this.loadKpis();
          this.$emit("update");
        } else {
          const err = await res.json().catch(() => ({}));
          this.$toast.error(err.error || "No se pudo fusionar");
        }
      } catch (e) {
        console.error("Error executing merge:", e);
        this.$toast.error("No se pudo completar la fusión");
      } finally {
        this.mergeLoading = false;
      }
    },
    currencyFormat(value) {
      const n = parseFloat(value) || 0;
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async searchRnc() {
      const responseRnc = await this.api_Post("accounting/consult_rnc", {
        rnc: this.editedItem.document_id,
      });
      let dataRnc = await responseRnc.json();
      if (dataRnc.length == 0) {
        this.rncValid = true;
        this.$toast.warning("El RNC no es valido");
        return false;
      }
      this.disabledName = true;
      this.rncValid = false;

      this.editedItem.name = dataRnc[0].name;
      return true;
    },
    showDialogStatus(item) {
      this.editedIndex = true;
      this.editedItem = Object.assign({}, item);
      this.dialogStatus = true;
    },
    editItem(item) {
      this.editedIndex = true;
      this.editedItem = Object.assign({}, item);
      this.bussiness_account = [];
      this.representatives = [];
      this.newRepresentative = { name: "" };
      let branches = [];
      if (this.editedItem.entity_branches != null) {
        for (let branch of this.editedItem.entity_branches) {
          branches.push(branch.supplier);
        }
      }
      if (item.supplier_payment_types != null) {
        this.selected_payment_methods = [];
        for (var payment of this.editedItem.supplier_payment_types) {
          this.selected_payment_methods.push(payment.id);
        }
      }
      this.selected_business_unit = this.editedItem.business_unit_id;
      this.editedItem.entity_branches = branches;
      this.selectedDocument = this.documents.find(
        (element) => element.value === this.editedItem.document_id_type_id
      );

      if (this.editedItem.currency_id != null) {
        let currencies_selected = this.currencies.find(
          (element) => element.id == this.editedItem.currency_id
        );
        this.seleted_currencies = currencies_selected.id;
      }

      if (this.editedItem.entity_contact_json == null) {
        this.itemContacts = [];
      } else {
        this.itemContacts = this.editedItem.entity_contact_json;
      }
      if (this.editedItem.entity_branches == null) {
        this.itemsSucursal = [];
      } else {
        this.itemsSucursal = this.editedItem.entity_branches;
      }
      if (this.editedItem.customer_details != null) {
        this.selectedtypesNcfCliente = this.editedItem.customer_details[0].customer.nfc_type_id;
        this.customer_type_id = this.editedItem.customer_details[0].customer.customer_type_id;
        this.originalCustomerTypeId = this.customer_type_id;
        this.selectedCreditDaysCliente = this.editedItem.customer_details[0].customer.credit_day_id;
        this.selectedWithholdingsCliente = this.editedItem.customer_details[0].customer.withholding_id;
        this.cxc =  this.editedItem.customer_details[0].customer.sale_chart_of_account_id;
        this.plazoPagoCliente = this.editedItem.customer_details[0].customer.credit_days;
        this.cliente = true;
      }
      if (this.editedItem.supplier_details != null) {
        this.selectedtypesNcfSuplidor = this.editedItem.supplier_details[0].supplier.nfc_type_id;
        this.selectedCreditDaysSuplidor = this.editedItem.supplier_details[0].supplier.credit_day_id;
        this.selectedWithholdingsSuplidor = this.editedItem.supplier_details[0].supplier.withholding_id;
        this.supplier_type_id= this.editedItem.supplier_details[0].supplier.supplier_type_id;
        this.originalSupplierTypeId = this.supplier_type_id;
        this.cxp = this.editedItem.supplier_details[0].supplier.purchase_chart_of_account_id;

        this.plazoPagoSuplidor = this.editedItem.supplier_details[0].supplier.credit_day_id;
        this.suplidor = true;

        if (this.editedItem.supplier_accounts != null) {
          for (let account of this.editedItem.supplier_accounts) {
            // Representantes: bank_id centinela (20). Van a su propia sección.
            if (account.bank_id == this.repBankId) {
              this.representatives.push({
                name: account.account_name,
                account: account.account,
              });
              continue;
            }
            let banc_text = this.itemsBanks.find(
              (element) => element.bank_id == account.bank_id
            );
            let tipeAcon_text = this.itemsAccountypes.find(
              (element) => element.id == account.account_type_id
            );

            this.bussiness_account.push({
              account_type_id: account.account_type_id,
              account_type_description: tipeAcon_text.description,
              account_bank_id: account.bank_id,
              bank_description_long: banc_text.bank_description_long,
              account_check_digit: account.account,
              account_name: account.account_name || this.editedItem.name,
              is_default: account.is_default,
            });
          }
        }
      }

      this.resetNewAccount();
      this.dialog = true;
    },
    async saveStatus() {
      const responseRequest = await this.api_Post("entity/update_status_entity", {
        entity_id: this.editedItem.entity_id,
        status_id: !this.editedItem.status_id
      })

      if (responseRequest.status == 200) {
        this.dialogStatus = false;
        this.loadKpis();
        this.$emit("update");
        this.$toast.success("Actualizado con éxito!");
      } else {
        this.$toast.error("Error al actualizar!");
      }
    },

    deleteItem(item) {
      this.editedIndex = this.entityData.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.entityData.splice(this.editedIndex, 1);
      this.closeDelete();
    },

    close() {
      this.dialog = false;
      this.cliente = false;
      this.suplidor = false;
      this.cxc = "";
      this.cxp = "";
      this.plazoPagoCliente = "";
      this.plazoPagoSuplidor = "";
      this.selectedProvincies = "";
      this.descriptionSucursal = "";
      this.addressSucursal = "";
      this.itemsSucursal = [];
      this.itemContacts = [];
      this.bussiness_account = [];
      this.representatives = [];
      this.newRepresentative = { name: "" };
      this.selectedDocument = "";
      this.selected_business_unit = null;
      this.customer_type_id = null;
      this.supplier_type_id = null;
      this.originalCustomerTypeId = null;
      this.originalSupplierTypeId = null;
      this.selectedtypesNcfCliente = null;
      this.selectedtypesNcfSuplidor = null;
      this.selectedCreditDaysCliente = null;
      this.selectedCreditDaysSuplidor = null;
      this.selectedWithholdingsCliente = null;
      this.selectedWithholdingsSuplidor = null;
      this.selected_payment_methods = null;
      this.seleted_currencies = null;
      this.selectedTypes = null;
      this.typeDescription = "";
      this.disabledName = false;
      this.rncValid = false;
      this.resetNewAccount();

      this.$nextTick(() => {
        this.editedItem = {};
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = {};
        this.editedIndex = -1;
      });
    },

    async save() {
      if (!this.editedItem.name) {
        this.$toast.warning("Debes indicar el nombre / razón social!");
        return;
      }

      if (!this.cliente && !this.suplidor) {
        this.$toast.warning("Debes marcar al menos Cliente o Suplidor!");
        return;
      }

      let suppliers_json = [];
      let customers_json = [];
      let supplier_accounts = [];
      let new_branches = [];
      for (var branch of this.itemsSucursal) {
        if (branch.id == null) {
          new_branches.push(branch);
        }
      }
      this.bussiness_account.forEach((account) => {
        supplier_accounts.push({
          supplier_id: this.editedItem.entity_id,
          bank_id: account.account_bank_id,
          account: account.account_check_digit,
          account_type_id: account.account_type_id,
          account_name: this.responsableToSave(account.account_name),
          is_default: account.is_default,
        });
      });

      // Representantes: misma tabla, con banco centinela y nombre tal cual.
      this.representatives.forEach((rep) => {
        supplier_accounts.push({
          supplier_id: this.editedItem.entity_id,
          bank_id: this.repBankId,
          account: rep.account,
          account_type_id: 1,
          account_name: rep.name,
          is_default: false,
        });
      });

      if (this.bussiness_account.length > 0) {
        let isAcccounDefault = this.bussiness_account.some((item) => item.is_default);
        if (isAcccounDefault == false) {
          this.$toast.warning("Debes selecionar una cuenta de banco por defecto!");
          return;
        }
      }

      if (this.suplidor) {
        let payments = [];
        if (this.selected_payment_methods != null) {
          for (let payment of this.selected_payment_methods) {
            payments.push({
              invoice_payment_type_id: payment,
            });
          }
        }

        suppliers_json = {
          supplier_type: this.supplier_type_id,
          credit_day_id: this.selectedCreditDaysSuplidor,
          nfc_type_id: this.selectedtypesNcfSuplidor,
          withholding_id: this.selectedWithholdingsSuplidor,
          supplier_accounts: supplier_accounts,
          supplier_payment_types: payments,
        };
      }

      if (this.cliente) {
        customers_json = {
          customer_type: this.customer_type_id,
          account_payable_id: this.cxc,
          credit_day_id: this.selectedCreditDaysCliente,
          nfc_type_id: this.selectedtypesNcfCliente,
          withholding_id: this.selectedWithholdingsCliente,
        };
      }

      let currencies_selected = this.currencies.find(
        (element) => element.id == this.seleted_currencies
      );
      if (this.editedItem.entity_id) {
        let data = {
          entity_id: this.editedItem.entity_id,
          document_id_type_id: this.selectedDocument ? this.selectedDocument.value : null,
          document_id: this.editedItem.document_id,
          name: this.editedItem.name.trimStart(),
          contact_json: this.itemContacts,
          is_supplier: this.suplidor,
          is_customer: this.cliente,
          currency_id: currencies_selected ? currencies_selected.id : null,
          business_unit_id: this.selected_business_unit,
          supplier_details: suppliers_json,
          customer_details: customers_json,
          branch_details: new_branches
        };

        const responseRequest = await this.api_Put("accounting/entity", data);
        if (responseRequest.status == 200) {
          this.$emit("update");
          this.$toast.success("actualizado con exito!");
          this.close();
        } else {
          this.$toast.error("error al agregar!");
        }
      } else {
        const responseRequest = await this.api_Post("accounting/entity", {
          entity_id: null,
          document_id_type_id: this.selectedDocument ? this.selectedDocument.value : null,
          document_id: this.editedItem.document_id,
          name: this.editedItem.name.trimStart(),
          contact_json: this.itemContacts,
          is_supplier: this.suplidor,
          is_customer: this.cliente,
          currency_id: currencies_selected ? currencies_selected.id : null,
          supplier_details: suppliers_json,
          customer_details: customers_json,
          branch_details:new_branches
        });

        if (responseRequest.status == 200) {
          this.$emit("update");
          this.$toast.success("agregado con exito!");
          this.close();
        } else {
          this.$toast.error("error al agregar!");
        }
      }
    },
  },
};
</script>

<style scoped>
.cs-link:hover {
  color: var(--v-primary-base) !important;
  text-decoration: underline;
}
.cs-stat {
  min-width: 130px;
}
.cs-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #455a64;
  padding: 14px 20px;
}
.cs-form-bar {
  position: sticky;
  top: 64px;
  z-index: 5;
}
@media (max-width: 599px) {
  .cs-form-bar {
    top: 56px;
  }
}
.cs-compare-bg {
  background-color: #f5f7fa;
}
.cs-compare-table td,
.cs-compare-table th {
  vertical-align: top;
}
.cs-compare-table th.cs-col-a,
.cs-compare-table td.cs-col-a {
  border-left: 3px solid #90caf9;
}
.cs-compare-table th.cs-col-b,
.cs-compare-table td.cs-col-b {
  border-left: 3px solid #ce93d8;
}
.cs-compare-diff td {
  background-color: #fff8e1;
}
.cs-compare-diff td:first-child {
  color: #f57f17 !important;
}
.cs-side-a {
  border-top: 3px solid #90caf9 !important;
}
.cs-side-b {
  border-top: 3px solid #ce93d8 !important;
}
.cs-compare-group td {
  background-color: #eceff1;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  border-bottom: 1px solid #cfd8dc !important;
}
</style>
